/**
 * Keeping a file's name honest about its bytes.
 *
 * The uploader re-encodes large images (browser-image-compression, usually to
 * JPEG) but used to keep the original filename, so a compressed `chart.png`
 * carried JPEG data. Two things downstream care:
 *
 *  - the server's upload filter checks the extension against the declared
 *    mimetype, so `.png` + `image/jpeg` is rejected outright;
 *  - AzureStorageService derives the blob's Content-Type from the extension, so
 *    the file would have been served as `image/png` regardless of its content.
 */

const EXTENSION_BY_MIME_TYPE: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
};

/**
 * Give a re-encoded file an extension that matches its new type.
 *
 * `.jpeg` is left alone — it already agrees with image/jpeg — and so is an
 * unknown type, since guessing would be worse than keeping the name the user
 * chose.
 */
export const renameToMatchType = (fileName: string, mimeType: string): string => {
  const wanted = EXTENSION_BY_MIME_TYPE[mimeType];
  if (!wanted || !fileName) return fileName;

  const dot = fileName.lastIndexOf('.');
  const current = dot === -1 ? '' : fileName.slice(dot + 1).toLowerCase();
  if (current === wanted || (wanted === 'jpg' && current === 'jpeg')) return fileName;

  const base = dot === -1 ? fileName : fileName.slice(0, dot);
  return `${base}.${wanted}`;
};

export default renameToMatchType;
